import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, 
  Download, 
  Trash2, 
  Loader2, 
  FileText, 
  Building, 
  User, 
  Calendar, 
  Edit, 
  Save, 
  X, 
  ZoomIn, 
  AlertTriangle, 
  Info,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const SOPDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, isAdmin } = useAuth();

  const [loading, setLoading] = useState(true);
  const [sop, setSop] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [expandedImage, setExpandedImage] = useState(null);

  useEffect(() => { fetchSop(); }, [id]);

  const fetchSop = async () => {
    try {
      const response = await api.get(`/sops/${id}`);
      setSop(response.data);
    } catch (error) {
      console.error('Failed to fetch SOP:', error);
      toast.error('Failed to load SOP');
      navigate('/sop-generator');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await api.get(`/sops/${id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SOP_${sop.name.replace(/\s+/g, '_')}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('SOP downloaded!');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download SOP');
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this SOP?')) return;
    try {
      await api.delete(`/sops/${id}`);
      toast.success('SOP deleted');
      navigate('/sop-generator');
    } catch (error) {
      toast.error('Failed to delete SOP');
    }
  };

  const handlePublish = async () => {
    try {
      await api.put(`/sops/${id}`, { status: 'Published' });
      setSop(prev => ({ ...prev, status: 'Published' }));
      toast.success('SOP published!');
    } catch (error) {
      toast.error('Failed to publish SOP');
    }
  };

  const handleUpdateStep = async (stepNumber, field, value) => {
    const updatedSteps = sop.steps.map(s =>
      s.step_number === stepNumber ? { ...s, [field]: value } : s
    );
    try {
      await api.put(`/sops/${id}`, { steps: updatedSteps });
      setSop(prev => ({ ...prev, steps: updatedSteps }));
      setEditingStep(null);
      toast.success('Step updated');
    } catch (error) {
      toast.error('Failed to update step');
    }
  };

  const canEdit = () => isAdmin() || sop?.created_by === user?.id;

  const getStatusColor = (status) => {
    switch (status) {
      case 'Published': return 'bg-emerald-100 text-emerald-700';
      case 'Draft': return 'bg-slate-100 text-slate-700';
      case 'Archived': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#235894]" />
      </div>
    );
  }

  if (!sop) return <div className="text-center py-16 text-slate-500">SOP not found</div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sop-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
                {sop.name}
              </h1>
              <Badge className={getStatusColor(sop.status)}>{sop.status}</Badge>
              <Badge variant="outline">v{sop.version}</Badge>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {sop.status === 'Draft' && canEdit() && (
            <Button variant="outline" onClick={handlePublish} className="border-emerald-500 text-emerald-600 hover:bg-emerald-50">
              <CheckCircle className="w-4 h-4 mr-2" /> Publish
            </Button>
          )}
          <Button onClick={handleDownload} disabled={downloading} className="bg-[#235894] hover:bg-[#1E4B80]" data-testid="download-sop-btn">
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Download Word
          </Button>
          {canEdit() && (
            <Button variant="outline" onClick={handleDelete} className="text-red-600 hover:text-red-700 hover:bg-red-50">
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
          )}
        </div>
      </div>

      {/* SOP Document Preview */}
      <div className="max-w-4xl mx-auto">
        {/* Document Header */}
        <Card className="mb-6 border-t-4 border-t-[#235894]">
          <CardContent className="pt-6">
            <h2 className="text-xl font-bold text-center text-[#235894] mb-4 font-['Plus_Jakarta_Sans']">
              {sop.name}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center gap-2 text-slate-600">
                <Building className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-400">Department</p>
                  <p className="font-medium">{sop.department || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <FileText className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-400">Version</p>
                  <p className="font-medium">{sop.version}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <User className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-400">Author</p>
                  <p className="font-medium">{sop.created_by_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Calendar className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-400">Date</p>
                  <p className="font-medium">{new Date(sop.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Purpose */}
        {sop.purpose && (
          <Card className="mb-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-[#235894]">1. Purpose</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed">{sop.purpose}</p>
            </CardContent>
          </Card>
        )}

        {/* Scope */}
        {sop.scope && (
          <Card className="mb-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-[#235894]">2. Scope</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed">{sop.scope}</p>
            </CardContent>
          </Card>
        )}

        {/* Prerequisites */}
        {sop.prerequisites?.length > 0 && (
          <Card className="mb-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-[#235894]">3. Prerequisites</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {sop.prerequisites.map((prereq, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-700">
                    <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {prereq}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Procedure Steps */}
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-[#235894]">4. Procedure</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {sop.steps?.map((step) => (
              <div key={step.step_number} className="border rounded-lg overflow-hidden" data-testid={`sop-step-${step.step_number}`}>
                {/* Step header */}
                <div className="bg-slate-50 px-4 py-3 flex items-center justify-between border-b">
                  <h4 className="font-semibold text-slate-800">
                    Step {step.step_number}: {step.title}
                  </h4>
                  {canEdit() && editingStep !== step.step_number && (
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingStep(step.step_number)}>
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>

                <div className="p-4 space-y-3">
                  {/* Description */}
                  {editingStep === step.step_number ? (
                    <div className="space-y-2">
                      <Textarea
                        defaultValue={step.description}
                        id={`edit-desc-${step.step_number}`}
                        rows={3}
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => {
                          const val = document.getElementById(`edit-desc-${step.step_number}`).value;
                          handleUpdateStep(step.step_number, 'description', val);
                        }}>
                          <Save className="w-3 h-3 mr-1" /> Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingStep(null)}>
                          <X className="w-3 h-3 mr-1" /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-700 leading-relaxed">{step.description}</p>
                  )}

                  {/* Screenshot */}
                  {step.screenshot_url && (
                    <div
                      className="relative rounded-lg overflow-hidden border bg-slate-900 cursor-pointer group"
                      onClick={() => setExpandedImage(step.screenshot_url)}
                    >
                      <img
                        src={step.screenshot_url}
                        alt={`Step ${step.step_number}`}
                        className="w-full max-h-80 object-contain"
                        data-testid={`sop-step-screenshot-${step.step_number}`}
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {step.notes && (
                    <div className="flex gap-2 p-3 bg-blue-50 rounded-lg text-sm">
                      <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium text-blue-700">Note: </span>
                        <span className="text-blue-800">{step.notes}</span>
                      </div>
                    </div>
                  )}

                  {/* Warning */}
                  {step.warning && (
                    <div className="flex gap-2 p-3 bg-amber-50 rounded-lg text-sm">
                      <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium text-amber-700">Warning: </span>
                        <span className="text-amber-800">{step.warning}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 py-4">
          Generated by Beyond Delta CI Platform | {new Date(sop.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
        </p>
      </div>

      {/* Screenshot Lightbox */}
      {expandedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setExpandedImage(null)}
          data-testid="sop-screenshot-lightbox"
        >
          <button className="absolute top-4 right-4 text-white hover:text-slate-300 transition-colors" onClick={() => setExpandedImage(null)}>
            <X className="w-8 h-8" />
          </button>
          <img src={expandedImage} alt="Screenshot full view" className="max-w-full max-h-full object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default SOPDetail;
